import { Clock, LogIn, LogOut, MapPin, OctagonAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatDistance } from "@/lib/format";
import {
  formatDuration,
  formatSignInTime,
  formatSignOutTime,
} from "@/lib/signins";

export type SignInActivityItemProps = {
  title: string;
  subtitle?: string | null;
  signInTime: Date | string | number;
  signOutTime?: Date | string | number | null;
  method: string;
  flagged?: boolean;
  distanceFromSite?: number | null;
  withinGeofence?: boolean | null;
};

export function SignInActivityItem({
  title,
  subtitle,
  signInTime,
  signOutTime,
  flagged,
  distanceFromSite,
  withinGeofence,
}: SignInActivityItemProps) {
  const toDate = (value: Date | string | number) =>
    value instanceof Date ? value : new Date(value);
  const toNullableDate = (value: Date | string | number | null | undefined) =>
    value == null ? null : toDate(value);

  const signInDate = toDate(signInTime);
  const signOutDate = toNullableDate(signOutTime);

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <div className="truncate text-sm font-semibold">{title}</div>
        <div className="flex shrink-0 items-center justify-end gap-1.5">
          {!signOutDate ? (
            <Badge variant="outline" size="sm">
              Active
            </Badge>
          ) : (
            <Badge variant="ghost" size="sm">
              Ended
            </Badge>
          )}
          {flagged && (
            <Badge variant="warning" size="sm">
              Flagged
            </Badge>
          )}
        </div>
      </div>

      {subtitle && (
        <div className="text-sm text-muted-foreground">{subtitle}</div>
      )}

      <div className="mt-3 space-y-1 text-sm">
        <div className="flex items-center gap-2">
          <LogIn size={16} />
          <span>
            Signed in:{" "}
            <span className="text-muted-foreground">
              {formatSignInTime(signInDate)}
            </span>
          </span>
        </div>

        {signOutDate && (
          <div className="flex items-center gap-2">
            <LogOut size={16} />
            <span>
              Signed out:{" "}
              <span className="text-muted-foreground">
                {formatSignOutTime(signOutDate, signInDate)}
              </span>
            </span>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Clock size={16} />
          {signOutDate ? (
            <span>
              Duration:{" "}
              <span className="text-muted-foreground">
                {formatDuration(signInDate, signOutDate)}
              </span>
            </span>
          ) : (
            <span>Active for: {formatDuration(signInDate, null)}</span>
          )}
        </div>
        {typeof withinGeofence === "boolean" && (
          <div className="flex items-center gap-2">
            <MapPin size={16} />
            <span>
              Geofence:{" "}
              <span
                className={
                  withinGeofence ? "text-muted-foreground" : "text-warning"
                }
              >
                {withinGeofence ? "Within boundary" : "Outside boundary"}
              </span>
            </span>
          </div>
        )}
        {typeof distanceFromSite === "number" && (
          <div className="mt-3 flex items-center gap-2 rounded-md border border-warning/25 bg-warning/12 px-2 py-1.5 text-warning-foreground">
            <OctagonAlert size={16} />
            <span>Signed in distance: {formatDistance(distanceFromSite)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default SignInActivityItem;
